/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollToPlugin } from 'gsap/all';
import { useLocation } from 'react-router';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(useGSAP, ScrollToPlugin);

type PageTransitionContextType = {
  transitionTo: (path: string) => void;
};

const PageTransitionContext = createContext<PageTransitionContextType | undefined>(undefined);

export const usePageTransition = () => {
  const ctx = useContext(PageTransitionContext);
  if (!ctx) throw new Error('usePageTransition must be used within PageTransitionProvider');
  return ctx;
};

export const PageTransitionProvider = ({ children }: { children: React.ReactNode }) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();

  const container = useRef<HTMLDivElement>(null);
  const { contextSafe } = useGSAP({ scope: container });

  const scrollToSelector = (selector: string, delay = 0) => {
    const target = document.querySelector(selector) as HTMLElement | null;
    if (!target) return false;

    const { top } = target.getBoundingClientRect();
    const absoluteTop = top + window.scrollY;
    const scrollMarginTop = parseFloat(getComputedStyle(target).scrollMarginTop || '0');
    const destination = absoluteTop - scrollMarginTop;

    gsap.to(window, {
      delay,
      duration: 1,
      ease: 'power2.out',
      scrollTo: { y: destination, autoKill: true },
    });
    return true;
  };

  const transitionTo = contextSafe((path: string) => {
    const [targetPath, targetHash] = path.split('#');

    if (targetPath === pathname) {
      if (hash !== targetHash) {
        scrollToSelector(`#${targetHash}`, 0.15);
        return;
      } else {
        return;
      }
    }

    if (pathname === path) return;

    if (!overlayRef.current) return;
    const overlay = overlayRef.current;

    gsap.to(overlay, {
      opacity: 1,
      duration: 0.5,
      ease: 'power2.out',
      onComplete: () => {
        navigate(path);
        gsap.to(overlay, {
          opacity: 0,
          duration: 0.6,
          ease: 'power2.in',
        });
      },
    });
  });

  useEffect(() => {
    if (!hash) return;

    let observer: MutationObserver | null = null;
    const cleanup = () => {
      if (observer) observer.disconnect();
      observer = null;
    };

    const startObserver = () => {
      observer = new MutationObserver(() => {
        if (scrollToSelector(hash)) cleanup();
      });

      observer.observe(document.body, { childList: true, subtree: true });
    };

    const timeoutId = window.setTimeout(() => {
      if (!scrollToSelector(hash)) {
        startObserver();
      }
    }, 200);

    return () => {
      cleanup();
      window.clearTimeout(timeoutId);
    };
  }, [hash, pathname]);

  return (
    <PageTransitionContext.Provider value={{ transitionTo }}>
      <div ref={container}>
        {children}
        <div
          ref={overlayRef}
          className="tw-fixed tw-inset-0 tw-bg-black tw-z-page-transition tw-pointer-events-none"
          style={{ opacity: 0 }}
        />
      </div>
    </PageTransitionContext.Provider>
  );
};
